import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Zap, Calendar, Trophy, Dumbbell, Activity, ArrowRight } from 'lucide-react'
import TennisHero from '../components/TennisHero'

const products = [
  {
    id: 'aicoach',
    name: 'Lafit智拍AI网球教练',
    tagline: 'AI 动作分析 · 职业动作对比',
    desc: '场馆多摄像头自动录制，AI 对比职业球员动作生成左右分屏对比视频和多维度分析报告。',
    icon: Zap,
    color: '#3b82f6',
    href: '/aicoach',
  },
  {
    id: 'venues',
    name: 'Lafit运动订场',
    tagline: '一键预约 · 轻松订场',
    desc: '覆盖多场馆的在线预订系统，支持场地查询、时段预约、在线支付。',
    icon: Calendar,
    color: '#10b981',
    href: '/venues',
  },
  {
    id: 'ladder',
    name: 'Lafit网球挑战榜',
    tagline: '挑战赛排行 · 积分竞技',
    desc: '网球挑战升降级系统，支持单打/双打挑战、实时积分排名。',
    icon: Trophy,
    color: '#f59e0b',
    href: '/ladder',
  },
  {
    id: 'gym',
    name: 'Lafit连锁智能健身房',
    tagline: '智能健身 · 无人值守',
    desc: '采用人脸识别门禁、智能跑步机，实现 24 小时无人值守健身房。',
    icon: Dumbbell,
    color: '#ec4899',
    href: 'https://gzzh.lafit.club',
    external: true,
  },
  {
    id: 'treadmill',
    name: 'Lafit智能跑步机',
    tagline: 'AI 陪跑 · 数据追踪',
    desc: '搭载 AI 教练系统的智能跑步机，实时监测运动数据。',
    icon: Activity,
    color: '#8b5cf6',
    href: 'https://sh.lafit.club',
    external: true,
  },
]

export default function Home() {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero ── 物理力场开屏 */}
      <section className="relative overflow-hidden" style={{ minHeight: '520px', height: 'calc(100vh - 64px)', maxHeight: '780px' }}>
        {/* Canvas 物理力场层 */}
        <TennisHero />

        {/* 底部渐变遮罩，承托文字 */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(6,6,15,0.90) 0%, rgba(6,6,15,0.55) 35%, transparent 70%)' }} />

        {/* 网格底纹 */}
        <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />

        {/* 前景文字内容，底部对齐 — pointer-events-none 让点击穿透到 canvas */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end px-4 text-center pb-10 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-5"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8' }}>
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              Lafit Technology
            </div>

            <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight drop-shadow-lg">
              运动科技&nbsp;<span className="gradient-text">智享未来</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-xl mx-auto mb-6 drop-shadow">
              专注运动数字化，AI + 智能硬件赋能体育产业
            </p>

            {/* 滚动提示 */}
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center gap-1 text-gray-500 text-xs"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4v12M4 10l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              向下探索产品
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Our Products</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white">全线产品矩阵</h2>
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {product.external ? (
                  <a
                    href={product.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block h-full rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                      border: `1px solid ${product.color}22`,
                    }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = `0 0 40px ${product.color}15`}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = 'none'}
                  >
                    <div className="p-5">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                        style={{ background: `${product.color}15`, color: product.color }}>
                        <product.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1">{product.name}</h3>
                      <p className="text-xs font-medium mb-2" style={{ color: product.color }}>{product.tagline}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{product.desc}</p>
                      <div className="mt-4 flex items-center gap-1 text-xs font-medium transition-all group-hover:gap-2"
                        style={{ color: product.color }}>
                        了解更多
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  </a>
                ) : (
                  <Link
                    to={product.href}
                    className="group block h-full rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                      border: `1px solid ${product.color}22`,
                    }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = `0 0 40px ${product.color}15`}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = 'none'}
                  >
                    <div className="p-5">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                        style={{ background: `${product.color}15`, color: product.color }}>
                        <product.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1">{product.name}</h3>
                      <p className="text-xs font-medium mb-2" style={{ color: product.color }}>{product.tagline}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{product.desc}</p>
                      <div className="mt-4 flex items-center gap-1 text-xs font-medium transition-all group-hover:gap-2"
                        style={{ color: product.color }}>
                        了解更多
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              联系我们
            </h2>
            <p className="text-gray-400 mb-6">
              期待与您的合作
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="tel:4008365001" className="text-gray-300 hover:text-white transition-colors">
                电话：400-8365-001
              </a>
              <span className="text-gray-600">·</span>
              <a href="mailto:info@lafit.club" className="text-gray-300 hover:text-white transition-colors">
                邮箱：info@lafit.club
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
